import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import Button from 'portal-modules/common/components/Button'
import { TaxDetails } from 'portal-modules/common/components'
import { RpModal } from 'portal-modules/common/components/modals'
import { LEDGER_TRANSACTION_TYPES } from 'portal-modules/payments/constants/types'
import { Document } from '@entrata/portals-common/lib/icons'
import {
  FocusSpan,
  underlineFocus,
} from 'portal-modules/common/components/focus-styles'
import getProjectName from 'portal-modules/common/utils/get-project-name'
import { InvoiceDocumentModal } from 'portal-modules/ledger/components/modals'
import { Overlay } from '@entrata/portals-common/lib/components'
import getDateForLocale from 'portal-modules/common/utils/get-date-for-locale'
import {
  StyledDivider
} from 'portal-modules/payments/components/transfermate/atoms'
import { connect } from 'react-redux'

const StyledSection = styled('section')`
  display: flex;
  justify-content: space-between;
  padding: ${({ hasTaxes }) => (hasTaxes ? '4px 0' : '8px 0')};
`

const StyledButton = styled(Button)`
  min-width: 100%;
`

const StyledTaxDisclaimer = styled('span')`
  color: ${({ theme }) => theme.gray.medium};
  display: flex;
  font-size: 12px;
  font-style: italic;
`

const StyledTaxAmountTotal = styled('section')`
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.gray?.medium || '#959595b8'};
  font-size: 12px;
  margin-top: 12px;
`

const StyledIconButton = styled('button')`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.secondary.base};

  width: 100%;

  ${underlineFocus};

  &:not(:last-child) {
    margin-right: 8px;
    margin-left: 8px;
  }

  svg {
    margin-right: 5px;
  }

  span {
    font-weight: bold;
    font-size: 16px;
  }
`

const TransactionDetailsModal = ({
  transactionDetails,
  onClose,
  currencyCode,
  transfermate,
  isLeaseAndRentWordReplaced = false,
  ...props
}) => {
  const [overlay, setOverlayToggle] = useState(false)

  const {
    amount = 0,
    date,
    title,
    fee_amount = 0,
    taxes = [],
    type,
    tax_exclusive,
    created_by_name,
    taxTotalAmt = 0,
    transaction_type,
    customer_invoice_id,
    document_id,
    token,
    history,
    provider,
  } = transactionDetails
  const { CHARGE, CREDIT, PAYMENT } = LEDGER_TRANSACTION_TYPES
  const isCharge = transaction_type == CHARGE.value
  const isCredit = transaction_type == CREDIT.value
  const isPayment = transaction_type == PAYMENT.value

  const totalAmt = isCharge
    ? Math.abs(amount) + Math.abs(taxTotalAmt)
    : isCredit
      ? amount + taxTotalAmt
      : Math.abs(amount) + Math.abs(fee_amount)

  const rentReplacedText = isLeaseAndRentWordReplaced ? __('Housing Fee Charges') : __('Charge Amount')

  return (
    <RpModal
      {...props}
      title={title}
      footerContent={
        <Button size='medium' fullWidth appearance='primary' onClick={onClose}>
          {__('Close')}
        </Button>
      }
      isWideDialog
    >
      { transfermate && transfermate?.instructions && (
        <>
          <div>
            <h3>{__('Payment Instructions')}</h3>
            <div
              dangerouslySetInnerHTML={{
                __html: transfermate?.instructions,
              }}
            />
          </div>
          <StyledDivider/>
        </>
      )}
      <div>
        {
          transfermate && transfermate?.instructions && (
            <h3>{__('Transaction Details')}</h3>
          )
        }
        <StyledSection>
          <span>{__('Transaction Date')}</span>
          <span>{getDateForLocale(date, 'MMM. D, YYYY')}</span>
        </StyledSection>
        {
          provider == 'apple_pay' && (
            <StyledSection>
              <span>{__('Processed By')}</span>
              <span>{provider.replace(provider, 'Apple Pay')}</span>
            </StyledSection>
          )
        }
        {isPayment && (
          <StyledSection>
            <span>{__('Time of Transaction')}</span>
            <span>{i18nUtils.datetime(date, 'TIME_SHORT')}</span>
          </StyledSection>
        )}
        {created_by_name && isPayment && (
          <StyledSection>
            <span>{__('Submitted By')}</span>
            <span>{__('{%s,0}', [created_by_name])}</span>
          </StyledSection>
        )}
        <StyledSection hasTaxes={taxTotalAmt != 0 && tax_exclusive}>
          <span>
            {isCharge
              ? rentReplacedText
              : isCredit
                ? __('Credit Amount')
                : __('Payment Amount')}
          </span>
          {taxTotalAmt != 0 && tax_exclusive ? (
            <span>{i18nUtils.currency(amount)}</span>
          ) : isPayment ? (
            <span>{i18nUtils.currency(amount)}</span>
          ) : (
            <span>{i18nUtils.currency(totalAmt)}</span>
          )}
        </StyledSection>
        {(isCharge || isCredit) &&
          taxTotalAmt != 0 &&
          tax_exclusive &&
          taxes &&
          !!taxes.length &&
          taxes.map(({ id, tax_amount, tax_label }) => (
            <TaxDetails
              key={id}
              tax_label={tax_label}
              tax_amount={tax_amount}
            />
          ))}
        {!isCharge && fee_amount > 0 && (
          <StyledSection>
            <span>
              {'CAD' === currencyCode
                ? __('Service Fee')
                : __('Convenience Fee')}
            </span>
            <span>{i18nUtils.currency(fee_amount)}</span>
          </StyledSection>
        )}
        {taxTotalAmt != 0 && !tax_exclusive && type != 'payment' && (
          <StyledTaxDisclaimer>
            <span>{__('Taxes included in charges')}</span>
          </StyledTaxDisclaimer>
        )}
        {!!taxTotalAmt && tax_exclusive && taxes && taxes.length > 1 && (
          <StyledTaxAmountTotal>
            <span>{__('Total Taxes')}</span>
            <span>{__('({%m, 0, p:2} tax)', [taxTotalAmt])}</span>
          </StyledTaxAmountTotal>
        )}
        {((isPayment && totalAmt && totalAmt > amount) ||
          isCharge ||
          isCredit) && (
            <StyledSection>
              <span>{isPayment ? __('Payment Total') : __('Total')}</span>
              <span>{i18nUtils.currency(totalAmt)}</span>
            </StyledSection>
          )}
        {!!customer_invoice_id &&
          !!document_id &&
          ['Charge', 'Credit', 'charge', 'credit'].includes(type) &&
          'rp' !== getProjectName() && (
            <>
              <hr />
              <StyledSection>
                <Overlay show={overlay} />
                <StyledIconButton onClick={() => setOverlayToggle(true)}>
                  <FocusSpan>
                    <Document size={15} />
                    {__('View Invoice')}
                  </FocusSpan>
                </StyledIconButton>
                {overlay && (
                  <InvoiceDocumentModal
                    show={overlay}
                    onClose={() => {
                      onClose()
                      setOverlayToggle(false)
                    }}
                    title={__('Customer Invoice - {%s, 0}', [
                      customer_invoice_id,
                    ])}
                    document_id={document_id}
                    token={token}
                    history={history}
                  />
                )}
              </StyledSection>
            </>
          )}
      </div>
    </RpModal>
  )
}

TransactionDetailsModal.propTypes = {
  transactionDetails: PropTypes.object,
  onClose: PropTypes.func,
  currencyCode: PropTypes.string,
  transfermate: PropTypes.object,
  isLeaseAndRentWordReplaced: PropTypes.bool
}

export default connect(
  ({
    productPermissions: { replace_lease_and_rent },
    propertyAssociation,
  }) => ({
    isLeaseAndRentWordReplaced:
      replace_lease_and_rent ||
      propertyAssociation?.resident_portal?.replace_lease_and_rent ||
      false,
  }),
)(TransactionDetailsModal)
